import { Flex } from '@chakra-ui/react';
import { useInfiniteScroll } from 'hooks/useInfiniteScrollHook';
import { TemplateHit } from 'hooks/usePaginatedTemplatesSearch/usePaginatedTemplatesSearch.types';
import { useResizeObserver } from 'hooks/useResizeObserver';
import { UserType } from 'modules/User';
import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { FixedSizeGrid } from 'react-window';

import { TemplateThumbnail } from './TemplateThumbnail';
import { COLUMN_NUM, PADDING_SIZE, ROW_HEIGHT, TEMPLATES_LIST_CLASS } from './TemplatesMenu.config';

interface TemplatesListProps {
  templates: TemplateHit[];
  onSelectTemplate: (templateData: TemplateHit) => void;
  userType: UserType;
  addPageIsDisabled: boolean;
  loadingTemplateId: string | null;
  loadMore: () => void;
  updatePreviousScrollTop: (scrollTop: number) => void;
  updateVisibleRowStartIdx: (visibleRowStartIndex: number) => void;
  initialScrollTop?: number;
}

/**
 * TEMPLATES LIST COMPONENT
 *
 * Renders a list of template thumbnails using react-window.
 */
export const TemplatesList = memo(
  ({
    templates,
    onSelectTemplate,
    userType,
    addPageIsDisabled,
    loadingTemplateId,
    loadMore,
    initialScrollTop,
    updatePreviousScrollTop,
    updateVisibleRowStartIdx,
  }: TemplatesListProps) => {
    const containerRef = useRef<HTMLDivElement>(null);

    // Ref for the outer grid container for accessing the scroll position
    const gridOuterRef = useRef<HTMLDivElement>(null);

    const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
    const [, onLoadAnchor] = useInfiniteScroll({ onLoadMore: loadMore, onLoadThreshold: 0.8 });
    useResizeObserver({
      ref: containerRef,
      onResize: ({ width = 0, height = 0 }) => {
        setContainerSize({ width, height });
      },
    });

    // When template is selected, update the previous scroll position + trigger prop
    const handleSelectTemplate = useCallback(
      (templateData: TemplateHit) => {
        onSelectTemplate(templateData);
        updatePreviousScrollTop(gridOuterRef.current?.scrollTop || 0);
      },
      [onSelectTemplate, updatePreviousScrollTop],
    );

    const gridData = useMemo(
      () => ({
        templates,
        onSelectTemplate: handleSelectTemplate,
        userType,
        addPageIsDisabled,
        loadingTemplateId,
        onLoadMoreAnchor: onLoadAnchor,
      }),
      [templates, handleSelectTemplate, userType, addPageIsDisabled, onLoadAnchor, loadingTemplateId],
    );

    // Store the first visible row index in a ref so that it can be focused correctly
    const onItemsRendered = ({ visibleRowStartIndex }: { visibleRowStartIndex: number }) => {
      updateVisibleRowStartIdx(visibleRowStartIndex);
    };

    return (
      <Flex ref={containerRef} h='full' w='full' direction='column' p={2} pt={0} overflowY='auto' overflowX='hidden'>
        <FixedSizeGrid
          columnCount={COLUMN_NUM}
          columnWidth={containerSize.width / COLUMN_NUM - PADDING_SIZE / 2}
          height={containerSize.height}
          rowCount={Math.ceil(templates.length / COLUMN_NUM)}
          width={containerSize.width}
          rowHeight={ROW_HEIGHT}
          itemData={gridData}
          outerRef={gridOuterRef}
          initialScrollTop={initialScrollTop}
          onItemsRendered={onItemsRendered}
          className={TEMPLATES_LIST_CLASS}
        >
          {TemplateThumbnail}
        </FixedSizeGrid>
      </Flex>
    );
  },
);

TemplatesList.displayName = 'TemplatesList';
